import { buildFilterUrl } from '@theme/atw-tyre-size-url';

/**
 * AutosTW custom — tyre-size search normalisation.
 *
 * Customers type a tyre size half a dozen different ways; the one the client
 * specifically asked for is the compressed all-digits form "2854522".
 * Shopify's native search tokenises "285/45R22" into 285 / 45 / 22 and ORs them,
 * so it answers with hundreds of unrelated tyres — and it finds nothing at all
 * for the compressed form.
 *
 * This module recognises the size, and routes the customer to the SAME native
 * metafield-filter URL the By-Size selector already builds, which is exact and
 * complete. Sizes we do not stock are deliberately left alone so the customer
 * falls through to normal search instead of landing on an empty page.
 *
 * The pure functions carry all the logic and are unit-tested with Vitest; the
 * DOM layer below is thin wiring that no-ops when the size index is absent.
 */

const BASE_PATH_FALLBACK = '/collections/tyres';
const INDEX_ID = 'atw-size-index';
const BRANDS_ID = 'atw-brand-index';
const ROW_CLASS = 'atw-size-hit';
const PANEL_CLASS = 'atw-size-panel';
const HIDDEN_CLASS = 'atw-wrong-size';
/**
 * Show every candidate when they fit, otherwise show a taste and say to keep typing.
 * Measured against the real catalogue: only 2 of 104 four-digit prefixes exceed 12
 * candidates, so a narrowed query almost always shows in full — which matters
 * because plain numeric order would otherwise bury the best-stocked size (typing
 * "2854" would hide 285/45R22 behind six 285/40 sizes).
 */
const FIT_ALL_ROWS = 12;
const TEASER_ROWS = 6;

/** Plausible passenger/4x4 ranges. Anything outside is a phone number, not a tyre. */
const RANGE = { width: [125, 405], profile: [20, 95], diameter: [10, 30] };

/**
 * Accepted shapes, checked against the query with all whitespace removed so that
 * "285 45 22" and "  285 / 45 R 22  " collapse onto the same handful of patterns.
 */
const PATTERNS = [
  /^(\d{3})[/-]?(\d{2})[/-]?[rz]{1,2}(\d{2})$/, // 285/45R22, 285-45-R22, 28545r22, 285/45ZR22
  /^(\d{3})[/-](\d{2})[/-](\d{2})$/, // 285/45/22, 285-45-22
  /^(\d{3})(\d{2})(\d{2})$/, // 2854522  <- the form the client asked for
];

/**
 * @typedef {{width: number, profile: number, diameter: number}} TyreSize
 */

/**
 * Parse a raw search query into a tyre size, or null when the query is not
 * *only* a tyre size. Returning null is the safe answer: it means "leave the
 * customer's search alone".
 *
 * @param {string} raw
 * @returns {TyreSize|null}
 */
export function parseTyreSize(raw) {
  if (typeof raw !== 'string') return null;
  const compact = raw.toLowerCase().replace(/\s+/g, '');
  if (!compact) return null;

  for (const pattern of PATTERNS) {
    const match = compact.match(pattern);
    if (!match) continue;
    const size = { width: +match[1], profile: +match[2], diameter: +match[3] };
    return inRange(size) ? size : null;
  }
  return null;
}

/**
 * @param {TyreSize} size
 * @returns {boolean}
 */
function inRange({ width, profile, diameter }) {
  const between = (/** @type {number} */ value, /** @type {number[]} */ [lo, hi]) => value >= lo && value <= hi;
  return between(width, RANGE.width) && between(profile, RANGE.profile) && between(diameter, RANGE.diameter);
}

/**
 * Render the canonical sidewall string, e.g. "285/45R22".
 *
 * @param {TyreSize} size
 * @returns {string}
 */
export function formatTyreSize({ width, profile, diameter }) {
  return `${width}/${String(profile).padStart(2, '0')}R${diameter}`;
}

/**
 * Is this a size we actually carry? Checked against the same generated combo
 * list the By-Size selector uses, because Shopify's predictive search happily
 * fuzzy-matches a size we do not stock onto a different rim diameter.
 *
 * @param {Array<[number, number, number]>|null|undefined} combos
 * @param {TyreSize|null} size
 * @returns {boolean}
 */
export function isStockedSize(combos, size) {
  if (!Array.isArray(combos) || !size) return false;
  return combos.some(([w, p, d]) => w === size.width && p === size.profile && d === size.diameter);
}

/**
 * The destination for a recognised size: the native storefront filter URL.
 *
 * @param {string} basePath
 * @param {TyreSize} size
 * @returns {string}
 */
export function sizeSearchUrl(basePath, size) {
  return buildFilterUrl(basePath, { width: size.width, profile: size.profile, diameter: size.diameter });
}

/** Digits plus the separators and R/Z that appear in tyre notation, nothing else. */
const SIZE_ISH = /^[\d/\s\-rz]+$/i;

/**
 * Turn a PARTIAL query into the real sizes it could still become.
 *
 * Shopify answers a half-typed size with "No results found for 2854", which reads
 * as broken and makes customers give up mid-size. This maps the digits onto the
 * catalogue's own size list so the panel always offers a next step.
 *
 * Digits are read structurally, not as a blind substring: "2854" is width 285 and
 * a profile starting with 4, while "4522" is profile 45 on a 22 inch rim. A load
 * rating (114), a model year (2016) or a phone number therefore match nothing and
 * are left entirely to normal search.
 *
 * @param {Array<[number, number, number]>|null|undefined} combos
 * @param {string} raw
 * @returns {{sizes: TyreSize[], known: {width?: number, profile?: number}}|null}
 */
export function suggestSizes(combos, raw) {
  if (!Array.isArray(combos) || !combos.length || typeof raw !== 'string') return null;

  const query = raw.trim();
  if (!query || !SIZE_ISH.test(query)) return null;

  const digits = query.replace(/\D/g, '');
  if (digits.length < 3 || digits.length > 7) return null;

  return byWidth(combos, digits) ?? byProfileAndRim(combos, digits);
}

/**
 * Read the digits as width, then profile, then rim diameter. When the full
 * reading matches nothing — a correctly formed size we simply do not carry — fall
 * back to the width alone rather than going silent on the customer.
 *
 * @param {Array<[number, number, number]>} combos
 * @param {string} digits
 */
function byWidth(combos, digits) {
  const width = Number(digits.slice(0, 3));
  if (!combos.some(([w]) => w === width)) return null;

  const profilePrefix = digits.slice(3, 5);
  const diameterPrefix = digits.slice(5, 7);
  const sameWidth = combos.filter(([w]) => w === width);
  const narrowed = sameWidth.filter(
    ([, p, d]) => `${p}`.padStart(2, '0').startsWith(profilePrefix) && `${d}`.startsWith(diameterPrefix)
  );

  const matches = narrowed.length ? narrowed : sameWidth;
  /** @type {{width?: number, profile?: number}} */
  const known = { width };
  const profile = Number(profilePrefix);
  if (profilePrefix.length === 2 && matches.every(([, p]) => p === profile)) known.profile = profile;

  return { sizes: toSizes(matches), known };
}

/**
 * Read the digits as profile plus rim diameter, which is how "4522" means a
 * 45 series tyre on a 22 inch rim. Only sensible while no width has been typed.
 *
 * @param {Array<[number, number, number]>} combos
 * @param {string} digits
 */
function byProfileAndRim(combos, digits) {
  if (digits.length > 4) return null;

  const profile = Number(digits.slice(0, 2));
  if (!combos.some(([, p]) => p === profile)) return null;

  const diameterPrefix = digits.slice(2, 4);
  const matches = combos.filter(([, p, d]) => p === profile && `${d}`.startsWith(diameterPrefix));
  if (!matches.length) return null;

  // "4522" pins both the profile and the rim; the width is still open, and a
  // filter URL is happy to leave it open too.
  const known = diameterPrefix.length === 2 ? { profile, diameter: Number(diameterPrefix) } : { profile };
  return { sizes: toSizes(matches), known };
}

/**
 * @param {Array<[number, number, number]>} combos
 * @returns {TyreSize[]}
 */
function toSizes(combos) {
  return [...combos]
    .sort((a, b) => a[0] - b[0] || a[1] - b[1] || a[2] - b[2])
    .map(([width, profile, diameter]) => ({ width, profile, diameter }));
}

/**
 * Split a query into the size and the brand it names, either of which may be absent.
 *
 * "2354518 Hankook" used to fall straight through to Shopify, which ORs the tokens
 * and answers a 235/45R18 question with 265/60R18 and 235/40R18 stock — one digit
 * from the size the customer asked for, on a product where that matters.
 *
 * Both halves are checked against what the shop actually carries, so a size or a
 * brand we do not stock reads as absent rather than as a match.
 *
 * @param {Array<[number, number, number]>|null|undefined} combos
 * @param {string[]|null|undefined} brands
 * @param {string} raw
 * @returns {{size: TyreSize|null, brand: string|null}}
 */
export function splitSizeAndBrand(combos, brands, raw) {
  const none = { size: null, brand: null };
  if (typeof raw !== 'string' || !raw.trim()) return none;

  let rest = raw;
  let brand = null;

  if (Array.isArray(brands)) {
    // Longest first so "BFGoodrich" is not shadowed by a shorter name inside it.
    for (const candidate of [...brands].sort((a, b) => b.length - a.length)) {
      const pattern = new RegExp(`\\b${candidate.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
      if (pattern.test(rest)) {
        brand = candidate;
        rest = rest.replace(pattern, ' ');
        break;
      }
    }
  }

  const size = findSize(rest);
  return { size: isStockedSize(combos, size) ? size : null, brand };
}

/**
 * parseTyreSize only accepts a string that is nothing but a size, which is right
 * for deciding whether to take a search over. Here the size sits among other
 * words, so try the whole remainder first — that keeps "285 45 22" working — and
 * then each word on its own.
 *
 * @param {string} text
 * @returns {TyreSize|null}
 */
function findSize(text) {
  const whole = parseTyreSize(text.trim());
  if (whole) return whole;
  for (const word of text.split(/\s+/)) {
    const size = parseTyreSize(word);
    if (size) return size;
  }
  return null;
}

/**
 * How many size rows to draw: all of them when they fit, otherwise a teaser.
 *
 * @param {TyreSize[]} sizes
 * @returns {number}
 */
export function visibleRows(sizes) {
  return sizes.length <= FIT_ALL_ROWS ? sizes.length : TEASER_ROWS;
}

/* ------------------------------------------------------------------ *
 * DOM layer — thin wiring. No-ops when the size index is not rendered.
 * ------------------------------------------------------------------ */

/** @type {{combos: Array<[number, number, number]>, brands: string[], basePath: string}|null} */
let index = null;

/** @type {WeakSet<Element>} */
const watched = new WeakSet();

/** @type {Map<string, {count: number, products: any[]}>} */
const previewCache = new Map();
let previewSeq = 0;

/**
 * What to show above the dropdown for the current query, if anything.
 *
 * @param {string} raw
 * @returns {{shortcut: {url: string, label: string}|null, sizes: TyreSize[], target: string|null}|null}
 */
function panelFor(raw) {
  if (!index) return null;

  const brandUrl = (brand) => buildFilterUrl(index.basePath, { brand });
  const combined = splitSizeAndBrand(index.combos, index.brands, raw);

  // A size and a brand together: offer both halves as well as the pair, rather than
  // guessing which one the customer would rather drop when the pair has no stock.
  if (combined.size && combined.brand) {
    const size = combined.size;
    const label = formatTyreSize(size);
    const both = buildFilterUrl(index.basePath, { ...size, brand: combined.brand });
    return {
      shortcuts: [
        { url: sizeSearchUrl(index.basePath, size), label: `All ${label} tyres` },
        { url: brandUrl(combined.brand), label: `All ${combined.brand} tyres` },
        { url: both, label: `${label} · ${combined.brand}` },
      ],
      sizes: [],
      size,
      brand: combined.brand,
      target: both,
    };
  }

  const exact = parseTyreSize(raw);
  if (isStockedSize(index.combos, exact)) {
    const stocked = /** @type {TyreSize} */ (exact);
    const url = sizeSearchUrl(index.basePath, stocked);
    return {
      shortcuts: [{ url, label: `View all ${formatTyreSize(stocked)} tyres` }],
      sizes: [],
      size: stocked,
      target: url,
    };
  }

  // A stocked size buried in a longer query still gets its shortcut, so the customer
  // never drops through to Shopify answering a size question with other sizes.
  if (combined.size) {
    const url = sizeSearchUrl(index.basePath, combined.size);
    return {
      shortcuts: [{ url, label: `View all ${formatTyreSize(combined.size)} tyres` }],
      sizes: [],
      size: combined.size,
      target: url,
    };
  }

  const suggestion = suggestSizes(index.combos, raw);
  if (!suggestion) return null;

  const label = shortcutLabel(suggestion.known);
  const shortcuts = label ? [{ url: buildFilterUrl(index.basePath, suggestion.known), label }] : [];
  const only = suggestion.sizes.length === 1 ? sizeSearchUrl(index.basePath, suggestion.sizes[0]) : null;
  return { shortcuts, sizes: suggestion.sizes, size: null, target: only ?? shortcuts[0]?.url ?? null };
}

/**
 * How to describe a partly-known size in the shortcut row.
 *
 * @param {{width?: number, profile?: number, diameter?: number}} known
 * @returns {string|null}
 */
function shortcutLabel({ width, profile, diameter }) {
  if (width && profile) return `View all ${width}/${profile} tyres`;
  if (width) return `View all ${width} tyres`;
  if (profile && diameter) return `View all ${profile}-series ${diameter} inch tyres`;
  return null;
}

/**
 * @param {EventTarget|null} target
 * @returns {target is HTMLInputElement}
 */
function isSearchInput(target) {
  return target instanceof HTMLInputElement && (target.name === 'q' || target.type === 'search');
}

/**
 * Rewrite the predictive-search request so the dropdown answers "2854522" with
 * the same products it would show for "285/45R22". Anything we do not recognise
 * is passed through untouched — search must never break because of this.
 */
function patchFetch() {
  const original = window.fetch;
  if (typeof original !== 'function' || window.__atwSizeSearchPatched) return;
  window.__atwSizeSearchPatched = true;

  window.fetch = function patchedFetch(input, init) {
    try {
      if (typeof input === 'string' && input.includes('/search/suggest')) {
        const url = new URL(input, window.location.origin);
        const query = url.searchParams.get('q');
        const size = query ? parseTyreSize(query) : null;
        if (isStockedSize(index?.combos, size)) {
          const canonical = formatTyreSize(/** @type {TyreSize} */ (size));
          if (canonical !== query) {
            url.searchParams.set('q', canonical);
            return original.call(this, url.toString(), init);
          }
        }
      }
    } catch {
      /* fall through to the original request */
    }
    return original.call(this, input, init);
  };
}

/**
 * @param {string} tag
 * @param {string} className
 * @param {string} [text]
 * @returns {HTMLElement}
 */
function element(tag, className, text) {
  const node = document.createElement(tag);
  node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

/**
 * Draw (or clear) the panel. It lives in the wrapper rather than the results
 * container so the theme's morph pass never fights us for it.
 *
 * @param {HTMLInputElement} input
 */
function syncPanel(input) {
  const wrapper = input.closest('.predictive-search-form')?.querySelector('.predictive-search-form__content-wrapper');
  if (!(wrapper instanceof HTMLElement)) return;

  const state = panelFor(input.value);
  const form = input.closest('.predictive-search-form');
  if (form) watchResults(form, input);
  const existing = wrapper.querySelector(`.${PANEL_CLASS}`);
  const signature = state
    ? `${state.shortcuts.map((s) => s.url).join('~')}|${state.sizes.map(formatTyreSize).join(',')}`
    : '';
  if (existing instanceof HTMLElement && existing.dataset.signature === signature) return;

  existing?.remove();
  wrapper.classList.toggle('atw-has-panel', Boolean(state));
  if (!state) return;

  const panel = element('div', PANEL_CLASS);
  panel.dataset.signature = signature;

  // The first row is the primary action; any others are alternatives the customer
  // can take when the combination they typed turns out to be empty.
  state.shortcuts.forEach((shortcut, i) => {
    const row = element('a', i === 0 ? ROW_CLASS : `${ROW_CLASS} ${ROW_CLASS}--alt`, shortcut.label);
    if (row instanceof HTMLAnchorElement) row.href = shortcut.url;
    panel.appendChild(row);
  });

  if (state.sizes.length) {
    panel.appendChild(element('p', `${PANEL_CLASS}__label`, 'Tyre sizes'));
    const list = element('ul', `${PANEL_CLASS}__list`);
    for (const size of state.sizes.slice(0, visibleRows(state.sizes))) {
      const item = document.createElement('li');
      const link = document.createElement('a');
      link.href = sizeSearchUrl(index.basePath, size);
      link.textContent = formatTyreSize(size);
      item.appendChild(link);
      list.appendChild(item);
    }
    panel.appendChild(list);
    const hidden = state.sizes.length - visibleRows(state.sizes);
    if (hidden > 0) {
      panel.appendChild(element('p', `${PANEL_CLASS}__more`, `+${hidden} more — keep typing`));
    }
  }

  wrapper.prepend(panel);
  if (form) syncResults(form, input);
}

/**
 * Decide what the results area should hold, once it actually holds something.
 *
 * Called again from the observer because the theme fetches results after the
 * keystroke: judging at input time always sees an empty list and would fill in
 * products Shopify was about to render correctly itself.
 *
 * @param {Element} form
 * @param {HTMLInputElement} input
 */
function syncResults(form, input) {
  const state = panelFor(input.value);
  const kept = pruneWrongSizes(form, state?.size ?? null);
  const panel = form.parentElement?.querySelector(`.${PANEL_CLASS}`) ?? document.querySelector(`.${PANEL_CLASS}`);
  if (!(panel instanceof HTMLElement)) return;

  panel.querySelectorAll(`.${PANEL_CLASS}__products, .${PANEL_CLASS}__fill`).forEach((node) => node.remove());
  if (state?.size && kept === 0) showRealProducts(panel, state.size, state.brand ?? null);
}

/**
 * Drop predictive-search cards that are not the size the customer asked for.
 *
 * Shopify's own relevance is unreliable on tyre sizes: measured over eight common
 * sizes it returned the right one every time for five, and for 235/45R18 and
 * 225/45R17 it returned eight cards of the neighbouring rim size instead. Rewriting
 * the query to the canonical form does not help. Showing a customer a 235/45R19 when
 * they typed 235/45R18 is the one mistake this shop cannot afford, so anything that
 * does not carry the size in its title is hidden, and the section goes with it when
 * nothing is left.
 *
 * @param {Element} form
 * @param {TyreSize|null} size
 * @returns {number} how many cards are still showing
 */
function pruneWrongSizes(form, size) {
  const section = form.querySelector('.predictive-search-results__products');
  if (!section) return 0;

  const cards = [...section.querySelectorAll('.predictive-search-results__card--product')];
  if (!cards.length) return 0;

  if (!size) {
    section.classList.remove(HIDDEN_CLASS);
    cards.forEach((card) => card.classList.remove(HIDDEN_CLASS));
    return cards.length;
  }

  const pattern = new RegExp(
    `${size.width}\\s*/\\s*${String(size.profile).padStart(2, '0')}\\s*[rz]{1,2}\\s*${size.diameter}(?!\\d)`,
    'i'
  );
  let kept = 0;
  for (const card of cards) {
    const matches = pattern.test(card.textContent || '');
    card.classList.toggle(HIDDEN_CLASS, !matches);
    if (matches) kept += 1;
  }
  section.classList.toggle(HIDDEN_CLASS, kept === 0);
  return kept;
}

/**
 * Fill the gap left by pruning with the products actually behind the filters.
 *
 * templates/collection.tyre-preview.liquid answers the same filtered URL the
 * shortcut links point at, as a few hundred bytes of JSON rather than the ~800KB
 * collection page.
 *
 * @param {HTMLElement} panel
 * @param {TyreSize} size
 * @param {string|null} brand
 */
async function showRealProducts(panel, size, brand) {
  const filters = brand ? { ...size, brand } : { ...size };
  const url = `${buildFilterUrl(index.basePath, filters)}&view=tyre-preview`;
  const seq = ++previewSeq;

  let data = previewCache.get(url);
  if (!data) {
    try {
      data = await fetch(url).then((r) => r.json());
    } catch {
      return;
    }
    previewCache.set(url, data);
  }
  // The customer kept typing while this was in flight.
  if (seq !== previewSeq || !panel.isConnected) return;

  // No stock in that brand and size: fall back to the size, which is the useful answer.
  if (!data.products?.length) {
    if (brand) showRealProducts(panel, size, null);
    return;
  }

  const list = element('ul', `${PANEL_CLASS}__products`);
  for (const product of data.products) {
    const item = document.createElement('li');
    const link = document.createElement('a');
    link.href = product.url;
    // 888 of the tyres carry no image; keep the slot so the row stays aligned.
    if (product.image) {
      const img = document.createElement('img');
      img.src = product.image;
      img.alt = '';
      img.loading = 'lazy';
      link.appendChild(img);
    } else {
      link.appendChild(element('span', `${PANEL_CLASS}__products-placeholder`));
    }
    link.appendChild(element('span', `${PANEL_CLASS}__products-title`, product.title));
    link.appendChild(element('span', `${PANEL_CLASS}__products-price`, product.price));
    item.appendChild(link);
    list.appendChild(item);
  }
  const heading = brand
    ? `${data.count} in ${formatTyreSize(size)} · ${brand}`
    : `${data.count} in ${formatTyreSize(size)}`;
  panel.appendChild(element('p', `${PANEL_CLASS}__label ${PANEL_CLASS}__fill`, heading));
  panel.appendChild(list);
}

/**
 * The theme morphs new results into place, which throws away the pruning, so
 * re-apply it whenever the results change. childList only: toggling a class is an
 * attribute change and would otherwise re-trigger this.
 *
 * @param {Element} form
 * @param {HTMLInputElement} input
 */
function watchResults(form, input) {
  const content = form.querySelector('.predictive-search-form__content');
  if (!(content instanceof HTMLElement) || watched.has(content)) return;
  watched.add(content);
  new MutationObserver(() => syncResults(form, input)).observe(content, { childList: true, subtree: true });
}

/**
 * Send the customer to the most specific listing they can already see, instead
 * of letting Shopify OR the digits together across the whole catalogue.
 *
 * @param {Event} event
 * @param {HTMLInputElement} input
 */
function takeOver(event, input) {
  const target = panelFor(input.value)?.target;
  if (!target) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  window.location.assign(target);
}

function init() {
  const indexElement = document.getElementById(INDEX_ID);
  if (!indexElement) return;

  /** @type {Array<[number, number, number]>} */
  let combos = [];
  try {
    combos = JSON.parse(indexElement.textContent || '[]');
  } catch {
    return;
  }
  if (!combos.length) return;

  /** @type {string[]} */
  let brands = [];
  try {
    brands = JSON.parse(document.getElementById(BRANDS_ID)?.textContent || '[]');
  } catch {
    brands = [];
  }

  index = { combos, brands, basePath: indexElement.getAttribute('data-base-path') || BASE_PATH_FALLBACK };

  patchFetch();

  document.addEventListener(
    'input',
    (event) => {
      if (isSearchInput(event.target)) syncPanel(event.target);
    },
    true
  );

  document.addEventListener(
    'keydown',
    (event) => {
      if (event.key !== 'Enter' || !isSearchInput(event.target)) return;
      takeOver(event, event.target);
    },
    true
  );

  document.addEventListener(
    'submit',
    (event) => {
      const input = /** @type {HTMLFormElement} */ (event.target).querySelector?.('input[name="q"]');
      if (input instanceof HTMLInputElement) takeOver(event, input);
    },
    true
  );
}

init();
